import { WorkOrderStatus } from "features/WorkOrders/constants/work-order.constants";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import type { WorkOrder } from "features/WorkOrders/types/work-order.types";

/** When true, show Hold / Resume in overview + list. Backend may already support them. */
export const WORK_ORDER_UI_HOLD_RESUME_ENABLED = true;

/** When true, show Close after completed. Backend may already support it. */
export const WORK_ORDER_UI_CLOSE_ENABLED = false;

/** Normalizes legacy API status values for lifecycle UI checks. */
export function normalizedWorkOrderStatus(
  status: string | null | undefined,
): string {
  const s = String(status ?? "").trim();
  if (s === "DRAFT") return WorkOrderStatus.NEW;
  if (s === "ASSIGNED") return WorkOrderStatus.NEW;
  return s;
}

const WO_CANCELLABLE_STATUSES: ReadonlySet<string> = new Set([
  WorkOrderStatus.NEW,
  WorkOrderStatus.IN_PROGRESS,
  WorkOrderStatus.ON_HOLD,
]);

export type WorkOrderLifecycleVisibility = {
  start: boolean;
  hold: boolean;
  resume: boolean;
  /** IN_PROGRESS → READY_FOR_BILLING (no separate completion-approval step). */
  approveForBilling: boolean;
  /** READY_FOR_BILLING → COMPLETED */
  submitBilling: boolean;
  close: boolean;
  cancel: boolean;
  /** COMPLETED only — cancels the current invoice and generates a corrected one. */
  regenerateInvoice: boolean;
};

/** Which lifecycle actions are allowed for this row (permissions + status). */
export function getWorkOrderLifecycleVisibility(
  workOrder: Pick<WorkOrder, "status">,
  can: (permission: string) => boolean,
): WorkOrderLifecycleVisibility {
  const raw = String(workOrder.status ?? "").trim();
  const normalized = normalizedWorkOrderStatus(workOrder.status);
  return {
    start:
      can(Permissions.WORK_ORDER.START) &&
      normalized === WorkOrderStatus.NEW,
    hold:
      WORK_ORDER_UI_HOLD_RESUME_ENABLED &&
      can(Permissions.WORK_ORDER.HOLD) &&
      raw === WorkOrderStatus.IN_PROGRESS,
    resume:
      WORK_ORDER_UI_HOLD_RESUME_ENABLED &&
      can(Permissions.WORK_ORDER.RESUME) &&
      raw === WorkOrderStatus.ON_HOLD,
    approveForBilling:
      can(Permissions.WORK_ORDER.APPROVE) &&
      raw === WorkOrderStatus.IN_PROGRESS,
    submitBilling:
      can(Permissions.WORK_ORDER.SUBMIT_BILLING) &&
      raw === WorkOrderStatus.READY_FOR_BILLING,
    close:
      WORK_ORDER_UI_CLOSE_ENABLED &&
      can(Permissions.WORK_ORDER.CLOSE) &&
      raw === WorkOrderStatus.COMPLETED,
    cancel:
      can(Permissions.WORK_ORDER.CANCEL) &&
      WO_CANCELLABLE_STATUSES.has(normalized),
    regenerateInvoice:
      can(Permissions.WORK_ORDER.REGENERATE_INVOICE) &&
      raw === WorkOrderStatus.COMPLETED,
  };
}

export function workOrderLifecycleAnyVisible(
  v: WorkOrderLifecycleVisibility,
): boolean {
  return (
    v.start ||
    v.hold ||
    v.resume ||
    v.approveForBilling ||
    v.submitBilling ||
    v.close ||
    v.cancel ||
    v.regenerateInvoice
  );
}
